import { FETCH_USER_DATA, RESET_PASSWORD } from 'dealer-reducers'
import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import EstigroupLogoSvg from '../other/svg/EstigroupLogoSvg'
import { useTranslation } from 'react-i18next'
import { RootReducer } from 'src/redux/rootReducer'
import { useNavigate, useParams } from 'react-router-dom'
import PasswordInput from '../atoms/PasswordInput'
import SignInButton from '../atoms/SignInButton'
import PasswordRequirementItem from '../atoms/PasswordRequirementItem'
import PasswordErrorMessage from '../atoms/PasswordErrorMessage'

const NewPassword = (): JSX.Element => {
  const { t } = useTranslation()
  const { token, email } = useParams()
  const [password, setPassword] = useState('')
  const [passwordRepeat, setPasswordRepeat] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const dispatch = useDispatch()
  const { resetPasswordFail, resetPasswordSuccess } = useSelector((state: RootReducer) => state.authReducer)

  const navigate = useNavigate()

  const passwordRequirements = useMemo(() => {
    const hasUpperCase = /[A-Z]/.test(password)
    const hasNumber = /[0-9]/.test(password)
    const hasSpecialChar = /[!#$%@*^&]/.test(password)
    const hasMinLength = password.length >= 8

    return {
      hasUpperCase,
      hasNumber,
      hasSpecialChar,
      hasMinLength,
      allMet: hasUpperCase && hasNumber && hasSpecialChar && hasMinLength
    }
  }, [password])

  const isPasswordValid = useMemo(() => {
    return (
      passwordRequirements.hasUpperCase &&
      passwordRequirements.hasNumber &&
      passwordRequirements.hasSpecialChar &&
      passwordRequirements.hasMinLength
    )
  }, [passwordRequirements])

  useEffect(() => {
    if (resetPasswordFail) {
      setPasswordError('unknown_error')
    }
    if (resetPasswordSuccess) {
      navigate('/')
      dispatch(FETCH_USER_DATA())
    }
  }, [resetPasswordFail, resetPasswordSuccess, navigate, t, dispatch])

  const getErrorMessage = (error: string | null): string => {
    if (error === 'passwordNotMatch') {
      return t('update_password_form.password_not_match')
    }
    if (error === 'unknown_error') {
      return t('update_password_form.unknown_error')
    }
    return t('update_password_form.password_do_not_meet_requirements')
  }

  const handleSubmit = (): void => {
    if (!isPasswordValid) {
      return
    }

    if (password !== passwordRepeat) {
      setPasswordError('passwordNotMatch')
      return
    }

    setPasswordError(null)
    dispatch(RESET_PASSWORD({ password, token: token ?? '', email: email ?? '' }))
  }

  return (
    <div className="w-screen h-screen pt-20 pb-9 bg-gradient-to-b from-sky-700 to-sky-800 inline-flex justify-center items-center gap-2.5">
      <div className="w-96 px-9 inline-flex flex-col justify-center items-start gap-16">
        <div className="w-full flex justify-center items-center">
          <EstigroupLogoSvg className="text-white" />
        </div>

        <div className="w-full max-w-96 flex flex-col justify-center items-center gap-6">
          <div className="self-stretch text-center justify-center text-white text-xl font-semibold font-Montserrat leading-6">
            Set new password
          </div>

          <div className="self-stretch flex flex-col justify-start items-start gap-8">
            <div className="self-stretch flex flex-col justify-start items-start gap-2">
              <div className="justify-center text-white text-sm font-medium font-Montserrat leading-5">
                {t('sign_in_screen.password')}
              </div>
              <PasswordInput
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                }}
                showPassword={showPassword}
                onTogglePassword={() => {
                  setShowPassword(!showPassword)
                }}
              />
            </div>

            <div className="self-stretch flex flex-col justify-start items-start gap-2.5">
              <div className="self-stretch flex flex-col justify-start items-start gap-1 flex-wrap content-start">
                <PasswordRequirementItem
                  isMet={passwordRequirements.hasUpperCase}
                  label={t('sign_in_screen.password_requirement_uppercase')}
                />
                <PasswordRequirementItem
                  isMet={passwordRequirements.hasNumber}
                  label={t('sign_in_screen.password_requirement_number')}
                />
                <PasswordRequirementItem
                  isMet={passwordRequirements.hasSpecialChar}
                  label={t('sign_in_screen.password_requirement_special_char')}
                />
                <PasswordRequirementItem
                  isMet={passwordRequirements.hasMinLength}
                  label={t('sign_in_screen.password_requirement_min_length')}
                />
                {passwordError !== null && <PasswordErrorMessage message={getErrorMessage(passwordError)} />}
              </div>
            </div>

            <div className="self-stretch flex flex-col justify-start items-start gap-2">
              <div className="self-stretch inline-flex justify-start items-center gap-1">
                <div className="justify-center text-white text-sm font-medium font-Montserrat leading-5">
                  {t('sign_in_screen.repeat_password')}
                </div>
              </div>
              <PasswordInput
                value={passwordRepeat}
                onChange={(e) => {
                  setPasswordRepeat(e.target.value)
                }}
                showPassword={showPasswordRepeat}
                onTogglePassword={() => {
                  setShowPasswordRepeat(!showPasswordRepeat)
                }}
              />
            </div>
          </div>

          <SignInButton onClick={handleSubmit} disabled={!isPasswordValid} isLoading={false} text="Confirm" />
        </div>
      </div>
    </div>
  )
}

export default NewPassword
